/* eslint-disable react/no-unescaped-entities */
import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import {
  MagnifyingGlass,
  CaretDown,
  ArrowRight,
  BookOpen,
  Sparkle,
  Info,
  Car as CarIcon,
  ArrowsClockwise,
  MapPin,
  Airplane,
  ClipboardText,
  Phone,
  Note,
  Money,
  Question,
} from "@phosphor-icons/react";

const SECTIONS = [
  {
    icon: Info,
    title: "مرحباً بك في مراسم الزوار",
    body: (
      <>
        <p><b>مراسم الزوار</b> منصة النقل الفاخر لزوار الحرمين الشريفين. نوفّر لك أسطولاً حديثاً من السيارات وسائقين محترفين لتنقلات مريحة وآمنة داخل المملكة.</p>
        <ul>
          <li>خدمة على مدار الساعة — 24/7.</li>
          <li>أسطول متنوع: لكزس، جمس، تاهو، هايس، دينالي، كوستر، وأكثر.</li>
          <li>تغطية شاملة: مكة المكرمة، جدة، الطائف، المدينة المنورة، والمزارات.</li>
        </ul>
      </>
    ),
  },
  {
    icon: ClipboardText,
    title: "كيف تنشئ حجز جديد",
    body: (
      <>
        <ol>
          <li>اضغط "حجز جديد" من الشاشة الرئيسية.</li>
          <li>أدخل بيانات العميل (الاسم + رقم الجوال مع مفتاح الدولة).</li>
          <li>اختر نقطة الانطلاق: المدينة + الموقع التفصيلي.</li>
          <li>اختر الوجهة: المدينة + الموقع التفصيلي.</li>
          <li>حدّد التاريخ (YYYY-MM-DD) والوقت (HH:mm بنظام 24 ساعة).</li>
          <li>اختر نوع السيارة والعدد المطلوب.</li>
          <li>حدّد عدد الركاب (إجباري).</li>
          <li>اختياري: أرفق صورة تذكرة الطيران وأضف ملاحظات.</li>
          <li>راجع السعر ثم اضغط "تأكيد الحجز".</li>
        </ol>
        <div className="help-tip">💡 يمكنك إضافة عدد غير محدود من المسارات لنفس الحجز.</div>
      </>
    ),
  },
  {
    icon: ArrowsClockwise,
    title: "رحلات ذهاب وإياب",
    body: (
      <>
        <ul>
          <li>اضغط "إضافة مسار جديد" داخل نموذج الحجز.</li>
          <li>استخدم زر <b>"العكس"</b> لتبديل الانطلاق والوجهة تلقائياً.</li>
          <li>كل مسار له تاريخه ووقته المستقل.</li>
          <li>يمكن إرفاق صورة تذكرة لكل مسار على حدة.</li>
        </ul>
      </>
    ),
  },
  {
    icon: MapPin,
    title: "خيارات الوجهة",
    body: (
      <>
        <p>الوجهات المدعومة حالياً:</p>
        <ul>
          <li><b>المدن الرئيسية:</b> مكة المكرمة، جدة، الطائف، المدينة المنورة.</li>
          <li><b>المزارات:</b> مزارات مكة (منى، عرفات، مزدلفة)، مزارات المدينة.</li>
          <li><b>الجولات السياحية:</b> جولة داخل المدينة المنورة تشمل المسجد النبوي والمعالم.</li>
        </ul>
      </>
    ),
  },
  {
    icon: CarIcon,
    title: "أنواع السيارات",
    body: (
      <>
        <ul>
          <li>🚗 <b>لكزس LX</b> — فاخرة، 4 ركاب.</li>
          <li>🚙 <b>هايس</b> — مناسبة للعوائل، 11 راكب.</li>
          <li>🚐 <b>دينالي / تاهو / سوبربان</b> — 6-7 ركاب.</li>
          <li>🚌 <b>كوستر</b> — للمجموعات، حتى 20 راكباً.</li>
          <li>🚙 <b>إكسبيدشن / فورد تورس</b> — 6 ركاب.</li>
        </ul>
        <div className="help-tip">💡 الأسعار تختلف حسب نوع السيارة والمسار — تظهر لك تلقائياً في نموذج الحجز.</div>
      </>
    ),
  },
  {
    icon: ClipboardText,
    title: "متابعة حجوزاتي",
    body: (
      <>
        <ul>
          <li>افتح تبويب "حجوزاتي" من الشاشة الرئيسية.</li>
          <li>ستجد كل حجوزاتك مرتبة حسب الحالة: قيد المراجعة، مؤكد، مكتمل، ملغى.</li>
          <li>اضغط على أي حجز لعرض التفاصيل الكاملة والخط الزمني للحالة.</li>
          <li>حمّل فاتورة PDF لأي حجز مكتمل بضغطة واحدة.</li>
        </ul>
      </>
    ),
  },
  {
    icon: Phone,
    title: "التواصل مع السائق",
    body: (
      <>
        <ol>
          <li>بعد تعيين السائق، يظهر اسمه ورقم جواله في تفاصيل الحجز.</li>
          <li>اضغط زر 📞 <b>اتصال</b> للمكالمة المباشرة.</li>
          <li>اضغط زر 📱 <b>واتساب</b> لفتح محادثة مع رسالة تعريفية جاهزة.</li>
          <li>السائق سيتواصل معك أيضاً قبل الرحلة بوقت كافٍ.</li>
        </ol>
      </>
    ),
  },
  {
    icon: Note,
    title: "تعديل أو إلغاء حجز",
    body: (
      <>
        <ul>
          <li>افتح تفاصيل الحجز من "حجوزاتي".</li>
          <li>اضغط <b>"إلغاء"</b> لإلغاء الحجز — يعمل قبل موعد الرحلة.</li>
          <li>للتعديلات الجوهرية (تغيير الموعد أو المسار) — تواصل مع الإدارة عبر واتساب.</li>
        </ul>
        <div className="help-tip">⚠️ يُفضّل الإلغاء قبل الموعد بـ 6 ساعات لتفادي أي رسوم.</div>
      </>
    ),
  },
  {
    icon: Money,
    title: "طرق الدفع والفواتير",
    body: (
      <>
        <ul>
          <li>الدفع النقدي عند نهاية الرحلة (المتاح افتراضياً).</li>
          <li>التحويل البنكي — تواصل مع الإدارة لتفاصيل الحساب.</li>
          <li>الشركات المتعاقدة: حساب شهري مع كشف حساب مفصّل.</li>
          <li>تنزيل فاتورة PDF لكل حجز من صفحة تفاصيله.</li>
        </ul>
      </>
    ),
  },
  {
    icon: Question,
    title: "خدمة العملاء والدعم",
    body: (
      <>
        <p>لأي استفسار أو دعم:</p>
        <ul>
          <li>📞 <b>مدير الحجوزات:</b> رقمه متاح في صفحة "عن الشركة".</li>
          <li>⏰ <b>ساعات العمل:</b> 24/7 لجميع الاستفسارات والحجوزات الطارئة.</li>
        </ul>
        <dl>
          <dt>كم مدة تأكيد الحجز؟</dt>
          <dd>عادةً خلال ساعة من الاستلام في أوقات الذروة، وأسرع في الأوقات العادية.</dd>
          <dt>هل يمكن الحجز قبل يوم واحد؟</dt>
          <dd>نعم — نقبل الحجوزات من ساعتين قبل الموعد وحتى شهر مسبقاً.</dd>
          <dt>ماذا لو تأخر السائق؟</dt>
          <dd>تواصل مع مدير الحجوزات فوراً — سيتم إرسال بديل عاجل أو إعادة جدولة.</dd>
          <dt>كيف نحصل على خصم للشركات؟</dt>
          <dd>الشركات المتعاقدة تحصل على أسعار خاصة — تواصل مع الإدارة لتفعيل التسعير الخاص.</dd>
        </dl>
      </>
    ),
  },
];

function AccordionItem({ section, defaultOpen = false, matched = false }) {
  const [open, setOpen] = useState(defaultOpen || matched);
  const Icon = section.icon;
  return (
    <div className={`help-section ${matched ? "help-matched" : ""}`}>
      <button
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        data-testid={`cli-help-toggle-${section.title}`}
        className="w-full flex items-center justify-between p-4 text-right"
      >
        <span className="inline-flex items-center gap-2 text-primary font-bold">
          <Icon size={18} weight="duotone" />
          <span>{section.title}</span>
        </span>
        <CaretDown size={16} weight="bold" className={`text-primary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="help-content px-5 pb-5">{section.body}</div>}
    </div>
  );
}

export default function ClientHelp() {
  const { user } = useAuth();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return SECTIONS.map((s) => ({ s, matched: false }));
    return SECTIONS.map((s) => {
      const inTitle = s.title.toLowerCase().includes(query);
      const bodyText = JSON.stringify(s.body).toLowerCase();
      return { s, matched: inTitle || bodyText.includes(query) };
    }).filter((x) => x.matched);
  }, [q]);

  // Admins have their own dedicated help; drivers use /driver/help.
  if (user?.role === "admin") return <Navigate to="/admin/help" replace />;
  if (user?.role === "driver") return <Navigate to="/driver/help" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-4xl mx-auto p-4 md:p-6">
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/"
            className="w-9 h-9 rounded-full border border-primary/30 text-primary hover:bg-primary/10 inline-flex items-center justify-center"
            data-testid="cli-help-back"
          >
            <ArrowRight size={16} weight="bold" />
          </Link>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white inline-flex items-center gap-2">
              <BookOpen size={26} weight="duotone" className="text-primary" />
              دليل الاستخدام
            </h1>
            <p className="text-white/50 text-sm mt-1">
              كل ما تحتاجه لتحجز رحلتك بسهولة وسرعة مع مراسم الزوار.
            </p>
          </div>
        </div>

        <div className="relative mb-6">
          <MagnifyingGlass size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-primary/60" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث في الدليل…"
            className="w-full pr-11 pl-4 py-3 rounded-xl bg-card border border-primary/25 text-white placeholder:text-white/40 focus:border-primary outline-none"
            data-testid="cli-help-search"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12 text-white/50 bg-card border border-primary/15 rounded-2xl">
            لا توجد نتائج تطابق "{q}"
          </div>
        ) : (
          <div className="space-y-3" data-testid="cli-help-sections">
            {filtered.map(({ s, matched }, i) => (
              <AccordionItem key={s.title} section={s} defaultOpen={i === 0 && !q} matched={matched && !!q} />
            ))}
          </div>
        )}

        <div className="mt-8 rounded-2xl p-5 bg-primary/8 border border-primary/25 text-center">
          <Sparkle size={22} weight="fill" className="text-primary mx-auto mb-2" />
          <div className="text-white font-bold text-sm">لم تجد ما تبحث عنه؟</div>
          <div className="text-white/60 text-xs mt-1">
            تواصل مع الإدارة من <Link to="/about" className="text-primary underline underline-offset-2">صفحة "عن الشركة"</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
